import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import Footer from "../components/common/Footer";
import Course_Slider from "../components/core/Catalog/Course_Slider";
import { getCatalogPageData } from "../services/operations/pageAndComponentData";
import { fetchCourseCategories } from "../services/operations/courseDetailsAPI";

/* ── Skeleton helpers ────────────────────────────────────────────── */
const Shimmer = ({ cls = "" }) => (
  <div className={`animate-pulse rounded-lg bg-richblack-700 ${cls}`} />
)

function HeroSkeleton() {
  return (
    <div className="box-content bg-richblack-800 px-4">
      <div className="mx-auto flex min-h-[260px] max-w-maxContentTab flex-col justify-center gap-4 lg:max-w-maxContent">
        <Shimmer cls="h-4 w-40" />
        <Shimmer cls="h-8 w-64 sm:w-96" />
        <Shimmer cls="h-3 w-full max-w-lg" />
        <Shimmer cls="h-3 w-4/5 max-w-md" />
      </div>
    </div>
  )
}

function SectionSkeleton({ showTabs = false }) {
  return (
    <div className="mx-auto box-content w-full max-w-maxContentTab px-4 py-12 lg:max-w-maxContent">
      <Shimmer cls="mb-6 h-6 w-56" />
      {showTabs && (
        <div className="mb-4 flex gap-6 border-b border-richblack-600 pb-2">
          <Shimmer cls="h-4 w-24" />
          <Shimmer cls="h-4 w-16" />
        </div>
      )}
      {/* 3-card skeleton grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className={`flex flex-col gap-3 ${i === 1 ? "hidden sm:flex" : ""} ${i === 2 ? "hidden lg:flex" : ""}`}>
            <Shimmer cls="h-44 w-full rounded-2xl" />
            <Shimmer cls="h-4 w-3/4" />
            <Shimmer cls="h-3 w-1/2" />
            <Shimmer cls="h-3 w-1/3" />
          </div>
        ))}
      </div>
    </div>
  )
}

/* ── Catalog page ────────────────────────────────────────────────── */
function Catalog() {
  const { catalogName } = useParams();
  const [active, setActive] = useState(1);
  const [catalogPageData, setCatalogPageData] = useState(null);
  const [categoryId, setCategoryId] = useState("");
  // Start true — avoids the false "No Courses found" flash on first render
  const [loading, setLoading] = useState(true);

  // Step 1: resolve the category ID from the URL slug
  useEffect(() => {
    setLoading(true);
    setCatalogPageData(null);
    setCategoryId(""); // clear stale ID from previous catalog

    (async () => {
      try {
        const categories = await fetchCourseCategories();
        const match = categories.find(
          (ct) => ct.name.split(" ").join("-").toLowerCase() === catalogName
        );
        if (match) {
          setCategoryId(match._id);
          // loading stays true — step 2 will fetch and then set false
        } else {
          setLoading(false); // category not in DB → show "not found"
        }
      } catch (error) {
        console.log("Could not fetch categories.", error);
        setLoading(false);
      }
    })();
  }, [catalogName]);

  // Step 2: fetch page data once we have the category ID
  useEffect(() => {
    if (!categoryId) return;

    (async () => {
      setLoading(true);
      try {
        const res = await getCatalogPageData(categoryId);
        setCatalogPageData(res);
      } catch (error) {
        console.log("Could not fetch catalog data.", error);
      }
      setLoading(false);
    })();
  }, [categoryId]);

  /* ── Loading skeleton ──────────────────────────────────────────── */
  if (loading) {
    return (
      <>
        <HeroSkeleton />
        <SectionSkeleton showTabs />
        <SectionSkeleton />
        <SectionSkeleton />
        <Footer />
      </>
    );
  }

  /* ── Empty / error state ───────────────────────────────────────── */
  if (!catalogPageData) {
    return (
      <div className="flex min-h-[calc(100vh-3.5rem)] flex-col items-center justify-center gap-4 text-center">
        <p className="text-2xl font-semibold text-richblack-5">
          No courses found
        </p>
        <p className="text-sm text-richblack-400">
          We couldn't find any courses for{" "}
          <span className="text-yellow-50 capitalize">{catalogName?.replace(/-/g, " ")}</span>.
        </p>
      </div>
    );
  }

  /* ── Page content ──────────────────────────────────────────────── */
  return (
    <>
      {/* Hero */}
      <div className="box-content bg-richblack-800 px-4">
        <div className="mx-auto flex min-h-[260px] max-w-maxContentTab flex-col justify-center gap-4 lg:max-w-maxContent">
          <p className="text-sm text-richblack-300">
            Home / Catalog /&nbsp;
            <span className="text-yellow-25">
              {catalogPageData?.selectedCategory?.name}
            </span>
          </p>
          <p className="text-3xl font-semibold text-richblack-5">
            {catalogPageData?.selectedCategory?.name}
          </p>
          <p className="max-w-[870px] text-richblack-200">
            {catalogPageData?.selectedCategory?.description}
          </p>
        </div>
      </div>

      {/* Section 1 — Courses to get you started */}
      <div className="mx-auto box-content w-full max-w-maxContentTab px-4 py-12 lg:max-w-maxContent">
        <p className="section_heading">Courses to get you started</p>
        <div className="my-4 flex border-b border-b-richblack-600 text-sm">
          <p
            onClick={() => setActive(1)}
            className={`cursor-pointer px-4 py-2 ${
              active === 1
                ? "border-b border-b-yellow-25 text-yellow-25"
                : "text-richblack-50"
            }`}
          >
            Most Popular
          </p>
          <p
            onClick={() => setActive(2)}
            className={`cursor-pointer px-4 py-2 ${
              active === 2
                ? "border-b border-b-yellow-25 text-yellow-25"
                : "text-richblack-50"
            }`}
          >
            New
          </p>
        </div>
        {/* eslint-disable-next-line react/jsx-pascal-case */}
        <Course_Slider Courses={catalogPageData?.selectedCategory?.courses ?? []} />
      </div>

      {/* Section 2 — Top courses in another category */}
      {catalogPageData?.differentCategory && (
        <div className="mx-auto box-content w-full max-w-maxContentTab px-4 py-12 lg:max-w-maxContent">
          <p className="section_heading">
            Top courses in {catalogPageData.differentCategory.name}
          </p>
          {/* eslint-disable-next-line react/jsx-pascal-case */}
          <Course_Slider Courses={catalogPageData.differentCategory.courses ?? []} />
        </div>
      )}

      {/* Section 3 — Frequently Bought */}
      <div className="mx-auto box-content w-full max-w-maxContentTab px-4 py-12 lg:max-w-maxContent">
        <p className="section_heading">Frequently Bought</p>
        {/* eslint-disable-next-line react/jsx-pascal-case */}
        <Course_Slider Courses={catalogPageData?.mostSellingCourses ?? []} />
      </div>

      <Footer />
    </>
  );
}

export default Catalog;
